import React, { useEffect, useState, useCallback } from "react"
import ReimbursementsList from "#/components/views/reimbursements/reimbursementsList/ReimbursementsList"
import Wrapper from "#/components/views/Wrapper"
import { BenefitResourcesSideBar } from "./BenefitResources"
import { useSelector } from "react-redux"
import { Redirect, withRouter } from "react-router-dom"
import { getUserInfoLoaded } from "#/redux/reducers/userInfo"
import { useToggle } from "#/utils/Hooks"
import { UpdateBankInfoModal } from "../../containers/reimbursements/UpdateBankInfoModal"
import { BenefitEnrollmentFlowContainer } from "#/components/cmd-enrollment"
import { useBenefitEnrollment } from "../../context/enrollment/BenefitEnrollmentContext"
import EnrollmentReminder from "./enrollment-reminders"
import { BenefitAvailabilityStatus } from "#/types/benefitEnrollmentStatus"
import { GetReimbursed } from "#/components/get-reimbursed"
import { useLocation, useHistory } from "react-router"
import { getSubsidies } from "#/redux/reducers/subsidies"
import { useCurrentUser } from "#/components/context/user/UserContext"
import EmployeeOnlySpaceContainer from "#/components/employee-only-containers/EmployeeOnlySpaceContainer"
import { useUserRole } from "../../partner-access/hooks/usePartnerAccess"
import { Typography, Button, Container, Stack, useTheme } from "@carrotfertility/carotene-core"
import { FormattedMessage, useIntl } from "react-intl"
import { PageLayout } from "#/features/global-ui/page-layout"
import { PageHeader, PageHeaderGlyph } from "#/features/global-ui/page-header"
import { ErrorPage } from "../app/ErrorPage"
import { createErrorBoundary } from "../../../utils/createErrorBoundary"
import { useShouldFillOutBankInfo } from "#/components/bank-details/hooks/useGetFundsAdministration"
import { TransactionDetailsDrawer } from "./TransactionDetailsDrawer"
import { useReimbursements } from "#/services/user/hooks/useReimbursements"
import { usePreAuthEligibility } from "#/components/carrot-card-cost-share/state/usePreAuthEligibility"
import { CarrotCardCostSharePreAuth } from "#/components/carrot-card-cost-share"

const PlanNotStarted = () => (
  <Wrapper medium>
    <div className="lh-copy overflow-hidden w-100 h-100 br3-ns pa5-ns pa3 black-80">
      <Typography
        color="rgba(0, 0, 0, 0.9)"
        fontFamily="Publico Headline"
        fontWeight="700"
        lineHeight="1.25"
        marginTop="4rem"
        fontSize="16px"
      >
        <FormattedMessage defaultMessage="Hang tight!" />
      </Typography>
      <Typography>
        <FormattedMessage defaultMessage="Once your Carrot benefit kicks in, you can start using it here." />
      </Typography>
    </div>
  </Wrapper>
)

const SubmitReimbursementsButton = withRouter(() => {
  const shouldShowBankInfoModal = useShouldFillOutBankInfo()
  const [isUpdateBankInfoModalOpen, toggleIsUpdateBankInfoModalOpen] = useToggle(false)
  const [isReimbursementsDialogOpen, toggleIsReimbursementsDialogOpen] = useToggle(false)
  const location = useLocation()

  const { availabilityStatus } = useBenefitEnrollment()

  const onNavigateToUpload = useCallback(() => {
    if (shouldShowBankInfoModal?.data) {
      toggleIsUpdateBankInfoModalOpen()
    } else {
      toggleIsReimbursementsDialogOpen()
    }
  }, [shouldShowBankInfoModal?.data, toggleIsUpdateBankInfoModalOpen, toggleIsReimbursementsDialogOpen])
  const isGetReimbursementsDisabled = availabilityStatus !== BenefitAvailabilityStatus.DOES_NOT_NEED_ENROLLMENT

  const [shouldRedirect, setShouldRedirect] = useState(false)

  useEffect(() => {
    if (location.hash === "#get-reimbursed" && !isReimbursementsDialogOpen) {
      if (isGetReimbursementsDisabled) {
        setShouldRedirect(true)
        return
      }
      location.hash = ""
      onNavigateToUpload()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- See https://carrotfertility.atlassian.net/wiki/spaces/PE/pages/2050295461/Remove+Build+Warnings#react-hooks%2Fexhaustive-deps
  }, [location.hash, isReimbursementsDialogOpen, isGetReimbursementsDisabled, onNavigateToUpload])

  if (shouldRedirect) {
    return <Redirect to="/transactions" />
  }

  return (
    <>
      <Button
        size="medium"
        onClick={onNavigateToUpload}
        color="secondary"
        variant="outlined"
        disabled={isGetReimbursementsDisabled}
      >
        <FormattedMessage defaultMessage="Get reimbursed" />
      </Button>
      {/* @ts-expect-error TS(2741) FIXME: Property 'onNavigateToAccountPage' is missing in t... Remove this comment to see the full error message */}
      {isUpdateBankInfoModalOpen && <UpdateBankInfoModal onClose={toggleIsUpdateBankInfoModalOpen} />}
      {isReimbursementsDialogOpen && (
        <GetReimbursed isOpen={isReimbursementsDialogOpen} onExit={toggleIsReimbursementsDialogOpen} />
      )}
    </>
  )
})

function AuthorizeCarrotCardButton(): JSX.Element {
  const history = useHistory()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const dialog = searchParams.get("dialog")

  const [openPreAuth, setOpenPreAuth] = useState(false)

  useEffect(() => {
    if (dialog === "authorize") {
      setOpenPreAuth(true)
      // remove 'dialog' param from URL
      const newParams = new URLSearchParams(location.search)
      newParams.delete("dialog")
      const newSearch = newParams.toString()
      const newPath = newSearch ? `${location.pathname}?${newSearch}` : location.pathname
      history.replace(newPath)
    }
  }, [dialog, history, location.search, location.pathname])

  return (
    <>
      <Button onClick={() => setOpenPreAuth(true)}>
        <FormattedMessage defaultMessage="Authorize Carrot Card" />
      </Button>
      <CarrotCardCostSharePreAuth open={openPreAuth} onClose={() => setOpenPreAuth(false)} />
    </>
  )
}

const ReimbursementHeader = () => {
  const intl = useIntl()
  const { isPartnerAccessingAccount } = useUserRole()
  const theme = useTheme()
  const preAuthEligibility = usePreAuthEligibility()
  const button =
    isPartnerAccessingAccount || preAuthEligibility.isLoading ? null : (
      <Stack
        direction={{ xs: "column-reverse", md: "row" }}
        gap={theme.spacing(theme.tokens.spacing.md)}
        width={{ xs: "100%", md: "auto" }}
      >
        <SubmitReimbursementsButton />
        {preAuthEligibility.data?.eligibleForPreAuth && <AuthorizeCarrotCardButton />}
      </Stack>
    )
  return (
    <Stack marginBottom={theme.spacing(theme.tokens.spacing.xl)} spacing={theme.spacing(theme.tokens.spacing.lg)}>
      <PageHeader
        startGlyph={
          <PageHeaderGlyph
            src="/images/icn-financial-support-header.png"
            srcSet={"/images/icn-financial-support-header-2x.png"}
          />
        }
        pageTitle={intl.formatMessage({ defaultMessage: "Your funds" })}
        endSlot={button}
      />
    </Stack>
  )
}

const TransactionsErrorPage = () => (
  <PageLayout>
    {/* @ts-expect-error TS(2769) FIXME: No overload matches this call. */}
    <Container justifyContent="center" alignItems="center" height="calc(100% - 133px)">
      <ErrorPage />
    </Container>
  </PageLayout>
)

const TransactionsPageErrorBoundary = createErrorBoundary(TransactionsErrorPage)
const ReimbursementsScreen = () => {
  const { availabilityStatus } = useBenefitEnrollment()
  const { refreshUserInfo } = useCurrentUser()
  const subsidies = useSelector(getSubsidies)
  const [showBenefitEnrollmentModal, toggleBenefitEnrollmentModal] = useToggle(false)
  const [showEnrollmentReminder, toggleEnrollmentReminder] = useToggle(
    availabilityStatus !== BenefitAvailabilityStatus.DOES_NOT_NEED_ENROLLMENT
  )
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [selectedTransaction, setSelectedTransaction] = useState<any>(null)

  useEffect(() => {
    refreshUserInfo()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- See https://carrotfertility.atlassian.net/wiki/spaces/PE/pages/2050295461/Remove+Build+Warnings#react-hooks%2Fexhaustive-deps
  }, [])

  const handleTransactionClick = (transaction: any) => {
    setSelectedTransaction(transaction)
    setDrawerOpen(true)
  }

  const handleDrawerClose = () => {
    setDrawerOpen(false)
    setSelectedTransaction(null)
  }

  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const expenseIdFromUrl = parseInt(params.get("expense-id"), 10)
  const { data: reimbursementsList = [] } = useReimbursements()
  useEffect(() => {
    if (expenseIdFromUrl) {
      const transaction = reimbursementsList.find((t: any) => t.expenseId === expenseIdFromUrl)
      if (transaction) {
        handleTransactionClick(transaction)
      }
    }
  }, [expenseIdFromUrl, reimbursementsList])

  return (
    <>
      <TransactionsPageErrorBoundary>
        <PageLayout variant="sidebar" sidebar={<BenefitResourcesSideBar />} header={<ReimbursementHeader />}>
          <EmployeeOnlySpaceContainer>
            <ReimbursementsList onTransactionClick={handleTransactionClick} />
          </EmployeeOnlySpaceContainer>
        </PageLayout>
        {showEnrollmentReminder && (
          <EnrollmentReminder
            availabilityStatus={availabilityStatus}
            onExit={() => toggleEnrollmentReminder()}
          ></EnrollmentReminder>
        )}
        {showBenefitEnrollmentModal && <BenefitEnrollmentFlowContainer onExit={toggleBenefitEnrollmentModal} />}
        <TransactionDetailsDrawer
          open={drawerOpen}
          onClose={handleDrawerClose}
          transaction={selectedTransaction}
          subsidies={subsidies}
        />
      </TransactionsPageErrorBoundary>
    </>
  )
}

const Transactions = () => {
  const subsidies = useSelector(getSubsidies)
  const userInfoLoaded = useSelector(getUserInfoLoaded)
  const { availabilityStatus } = useBenefitEnrollment()

  if (availabilityStatus === BenefitAvailabilityStatus.NEEDS_BENEFIT_ENROLLMENT) {
    return <Redirect to={"/#needs-benefit-enrollment"} />
  }

  if (userInfoLoaded) {
    if (!subsidies?.some((s) => s.planStarted)) {
      return <PlanNotStarted />
    }
    return <ReimbursementsScreen />
  } else {
    return null
  }
}

export default Transactions
